import { format } from 'date-fns';
import { Auth, User } from '../../../utils/types';
import UserAvatar from '../UserAvatar/UserAvatar';
import Title from '../Title/Title';
import TextField from '../TextField/TextField';
import { useTranslation } from '../../../hooks/translations/TranslationProvider';
import Icon from '../Icon/Icon';
import { COLOURS } from '../../../utils/consts';
import ProgresssBar from '../ProgressBar/ProgressBar';
import Button from '../Button/Button';
import { useAppSelector } from '../../../store/hooks';

import './userDetails.css';
import { UserReport } from '../../UserPage/useUserPage';

interface UserDetailsProps {
    user: User | Auth;
    onFollowUser?: () => void;
    onFollowersClick?: () => void;
    userReports: UserReport[];
}

const UserDetails: React.FC<UserDetailsProps> = ({
    user,
    onFollowUser,
    onFollowersClick,
    userReports,
}) => {
    const auth = useAppSelector(state => state.auth);

    const { t } = useTranslation();

    const title = `${user.name} ${user.surname}`;

    function onFollowersClickHandler() {
        if (user.userFollowers > 0 && onFollowersClick) {
            onFollowersClick();
        } else return;
    }

    return (
        <div className="user-details">
            <div className="user-info">
                <UserAvatar
                    imageSrc={user.avatarUrl}
                    size="xl"
                    averageRating={user.averageRating}
                />
                <div className="user-info-wrapper">
                    <div className="user-main">
                        <Title text={title} size="xl" />
                        <div className="flex">
                            <TextField size="s" color="green">
                                @
                            </TextField>
                            <TextField weight="bold" size="s">
                                {user.username}
                            </TextField>
                        </div>
                        <div className="user-followers-wrapper">
                            <div>
                                <Icon name="bx-group" color="green" />
                                <span onClick={onFollowersClickHandler}>
                                    <TextField size="s">Followers: </TextField>
                                    <TextField
                                        color="green"
                                        weight="bold"
                                        size="s"
                                    >
                                        {user.userFollowers}
                                    </TextField>
                                </span>
                            </div>
                            {user.id !== auth.authData?.id && (
                                <Button
                                    text={`${
                                        user.isFollowing ? 'Unfollow' : 'Follow'
                                    }`}
                                    size="sm"
                                    variant={`${
                                        user.isFollowing
                                            ? 'transparent-green'
                                            : 'transparent'
                                    }`}
                                    onClick={onFollowUser}
                                />
                            )}
                        </div>
                        {userReports.length > 0 && (
                            <div className="flex gap">
                                <Icon name="bx-error" color={COLOURS.RED} />
                                <TextField color="red" size="s">{`${t(
                                    'userDetails.reports'
                                )} ${userReports.length}`}</TextField>
                            </div>
                        )}
                        <div className="user-main-section">
                            <Icon name="bx-map" color="green" />
                            <TextField size="s">{`${user.city.name}, ${user.country.name}`}</TextField>
                        </div>
                        <div className="user-main-section">
                            <Icon name="bx-calendar" color="green" />
                            <TextField size="s">
                                {`${t('userDetails.memberSince')} 
                                ${format(
                                    new Date(user.createdAt),
                                    'dd/MM/yyyy'
                                )}`}
                            </TextField>
                        </div>
                    </div>
                </div>
            </div>
            <div className="user-stats">
                <div>
                    <Title
                        color="green"
                        size="xl"
                        text={user.stats.organized.toString()}
                    />

                    <TextField size="s">{t('userDetails.created')}</TextField>
                </div>
                <div>
                    <Title
                        color="green"
                        size="xl"
                        text={user.stats.appearances.toString()}
                    />
                    <TextField size="s">{t('userDetails.joined')}</TextField>
                </div>
                {/* <div>
                            <TextField color="green" size="xl" weight="bold">
                                {user.stats.mvp}
                            </TextField>
                            <TextField size="s">MVP</TextField>
                        </div> */}
                <div>
                    <Title
                        color="green"
                        size="xl"
                        text={user.stats.victories.toString()}
                    />
                    <TextField size="s">{t('userDetails.victories')}</TextField>
                </div>
                <div>
                    <Title
                        color="green"
                        size="xl"
                        text={user.stats.defeats.toString()}
                    />
                    <TextField size="s">{t('userDetails.defeats')}</TextField>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;
